import { useEffect, useState, useCallback } from "react";
import { Activity, Server, Users, AlertTriangle, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

function Tile({ icon: Icon, label, value, sub, tone = "default", testId }) {
  const toneRing = tone === "danger" ? "ring-red-500/20"
    : tone === "success" ? "ring-emerald-500/20"
    : "ring-border";
  return (
    <Card className={`flex flex-col justify-between p-5 shadow-sm ring-1 ${toneRing}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
      </div>
      <div className="mt-3">
        <div className="font-heading text-3xl font-bold tracking-tight text-foreground">{value}</div>
        {sub ? <div className="mt-1 text-xs text-muted-foreground">{sub}</div> : null}
      </div>
    </Card>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pinging, setPinging] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  const pingAll = useCallback(async () => {
    setPinging(true);
    try {
      const { data } = await api.post("/dashboard/health-check");
      toast.success(`Health-checked ${data.results.length} server(s)`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPinging(false); }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Live overview of SSO handoffs, mapping counts, and WHM server health."
        testId="dashboard-header"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="dashboard-refresh-btn">
              <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={pingAll} disabled={pinging} data-testid="dashboard-ping-all-btn">
              <Activity className="mr-1.5 h-4 w-4" />
              {pinging ? "Pinging…" : "Ping all servers"}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          icon={Server} label="WHM Servers"
          value={stats?.servers?.total ?? "—"}
          sub={stats?.servers ? `${stats.servers.healthy ?? 0} healthy · ${stats.servers.down ?? 0} down` : ""}
          testId="tile-servers"
        />
        <Tile
          icon={Users} label="Active Mappings"
          value={stats?.mappings?.total ?? "—"}
          sub={"Halo users routed to cPanel"}
          testId="tile-mappings"
        />
        <Tile
          icon={Activity} label="SSO Success (24h)"
          value={stats?.sso_24h ? `${stats.sso_24h.success_rate ?? 0}%` : "—"}
          sub={stats?.sso_24h ? `${stats.sso_24h.success ?? 0} ok / ${stats.sso_24h.failure ?? 0} failed` : ""}
          tone={stats?.sso_24h && stats.sso_24h.failure > 0 && stats.sso_24h.success_rate < 90 ? "danger" : "success"}
          testId="tile-success-rate"
        />
        <Tile
          icon={AlertTriangle} label="Failed (7d)"
          value={stats?.sso_7d?.failure ?? "—"}
          sub={stats?.sso_7d ? `${stats.sso_7d.success ?? 0} successful` : ""}
          tone={stats?.sso_7d && stats.sso_7d.failure > 0 ? "danger" : "default"}
          testId="tile-failed-7d"
        />
      </div>

      <Card className="mt-6" data-testid="recent-failures-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="font-heading text-sm font-semibold uppercase tracking-[0.06em] text-foreground">Recent failed attempts</h2>
          <StatusPill status={stats && stats.recent_failures?.length ? "failure" : "healthy"}
            label={stats && stats.recent_failures?.length ? `${stats.recent_failures.length} recent` : "All clear"} />
        </div>
        <div className="divide-y divide-border">
          {(stats?.recent_failures || []).length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">
              No failed SSO attempts. Nice.
            </div>
          ) : (
            stats.recent_failures.map((f) => (
              <div key={f.id} className="grid grid-cols-1 gap-1 px-5 py-3 text-sm sm:grid-cols-[180px_1fr_120px] sm:items-center" data-testid={`failure-row-${f.id}`}>
                <div className="font-mono text-xs text-muted-foreground">{new Date(f.created_at).toLocaleString()}</div>
                <div>
                  <div className="font-medium">{f.halo_user_ref || "—"}</div>
                  <div className="text-xs text-muted-foreground">{f.message}</div>
                </div>
                <div className="text-right text-xs text-muted-foreground">{f.source_ip}</div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
